import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Phone, Menu, X } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import SEO from '../../components/SEO';
import NotFound from '../notFound/NotFound';
import { getAreaContent, DEFAULT_CONTENT } from '../../data/areaContent';
import { getCanonicalAreaName, toArabicSlug } from '../../data/areaSlugMapping';
import './AreaDetail.css';

import MajorDistrictTemplate from './templates/MajorDistrictTemplate';
import HighwayTemplate from './templates/HighwayTemplate';
import MidTierTemplate from './templates/MidTierTemplate';
import NewCityTemplate from './templates/NewCityTemplate';
import GovernorateTemplate from './templates/GovernorateTemplate';

// Import WebP images
import wa1 from '../../images/WhatsApp Image 2025-07-02 at 15.02.33_fe153007.webp';
import wa2 from '../../images/WhatsApp Image 2025-07-02 at 15.02.33_732c1eeb.webp';
import wa3 from '../../images/WhatsApp Image 2025-07-02 at 15.02.33_0b194e2d.webp';
import wa4 from '../../images/WhatsApp Image 2025-07-02 at 15.02.32_bc1bb19d.webp';
import wa5 from '../../images/WhatsApp Image 2025-07-02 at 15.02.32_00a9c303.webp';

// Helper function to decide template
const getTemplateType = (areaName) => {
  if (!areaName) return 'MajorDistrict';
  if (
    areaName.includes('محور') ||
    areaName.includes('طريق') ||
    areaName.includes('دائري') ||
    areaName.includes('دائرى') ||
    areaName.includes('الأوتوستراد') ||
    areaName.includes('اوتوستراد') ||
    areaName.includes('صلاح سالم')
  ) {
    return 'Highway';
  }
  if (
    areaName.includes('زايد') ||
    areaName.includes('أكتوبر') ||
    areaName.includes('اكتوبر')
  ) {
    return 'NewCity';
  }
  if (areaName === 'زهراء المعادي' || areaName === 'صقر قريش') {
    return 'MidTier';
  }
  return 'MajorDistrict';
};

const AreaDetail = () => {
  const { areaName } = useParams();
  const canonicalName = getCanonicalAreaName(areaName);
  const rawContent = canonicalName ? getAreaContent(canonicalName) : null;

  const [activeSection, setActiveSection] = useState('header');
  const [isNavigatorOpen, setIsNavigatorOpen] = useState(false);

  // Navigation sections
  const sections = useMemo(() => [
    { id: 'header', title: 'الرئيسية', icon: '🏠' },
    { id: 'local-coverage', title: 'المعالم والمحاور', icon: '📍' },
    { id: 'contact', title: 'طرق التواصل', icon: '📞' },
    { id: 'gallery', title: 'معرض الصور', icon: '📸' },
    { id: 'services', title: 'خدماتنا', icon: '🚗' },
    { id: 'faq', title: 'الأسئلة الشائعة', icon: '❓' }
  ], []);

  // Update metadata when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [areaName]);

  // Handle scroll spy
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;

      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections[i].id);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  // If the area is not recognized in our database, render 404
  if (!canonicalName || !rawContent) {
    return <NotFound />;
  }

  const displayName = canonicalName;
  const phone = '01044844492';
  const brand = 'خصم 50% على ونش انقاذ محمد بحر';
  const areaData = { ...DEFAULT_CONTENT, ...rawContent, title: `ونش انقاذ ${displayName}` };
  const templateType = getTemplateType(displayName);

  // Handle scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(sectionId);
      setIsNavigatorOpen(false);
    }
  };

  const AREA_COORDINATES = {
    'مدينة نصر': { lat: 30.0561, lng: 31.3364, region: 'القاهرة' },
    'التجمع الخامس': { lat: 30.0131, lng: 31.4289, region: 'القاهرة' },
    'مصر الجديدة': { lat: 30.0911, lng: 31.3256, region: 'القاهرة' },
    '6 أكتوبر': { lat: 29.9722, lng: 30.9392, region: 'الجيزة' },
    'الدقي': { lat: 30.0385, lng: 31.2118, region: 'الجيزة' },
    'المعادي': { lat: 29.9599, lng: 31.2595, region: 'القاهرة' },
    'المهندسين': { lat: 30.0571, lng: 31.2013, region: 'الجيزة' },
    'الشيخ زايد': { lat: 30.0219, lng: 30.9739, region: 'الجيزة' },
    'الهرم': { lat: 29.9974, lng: 31.1486, region: 'الجيزة' },
    'فيصل': { lat: 30.0075, lng: 31.1712, region: 'الجيزة' },
  };

  const coords = AREA_COORDINATES[displayName];

  // Advanced Schema Markup
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "AutoTowingService",
    "name": `ونش انقاذ ${displayName} - محمد بحر`,
    "description": areaData.metaDescription,
    "telephone": "+201044844492",
    "areaServed": {
      "@type": "AdministrativeArea",
      "name": displayName
    },
    ...(coords ? {
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": coords.lat,
        "longitude": coords.lng
      },
      "address": {
        "@type": "PostalAddress",
        "addressLocality": displayName,
        "addressRegion": coords.region,
        "addressCountry": "EG"
      }
    } : {}),
    "priceRange": "250 - 500 EGP",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": `خدمات ونش انقاذ ${displayName}`,
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": `سحب سيارة ملاكي داخل ${displayName}`
          },
          "price": "250",
          "priceCurrency": "EGP"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": `ونش سطحة هيدروليكية مجهزة في ${displayName}`
          },
          "price": "350",
          "priceCurrency": "EGP"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": `مساعدة على الطريق (شحن بطارية / إطارات / وقود) في ${displayName}`
          },
          "price": "150",
          "priceCurrency": "EGP"
        }
      ]
    },
    "url": `https://winchmohamedbahr.com/areas/${toArabicSlug(canonicalName)}`,
    "openingHours": "Mo-Su 00:00-24:00",
    "image": "https://winchmohamedbahr.com/logo.png"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "الرئيسية",
        "item": "https://winchmohamedbahr.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "مناطق الخدمة",
        "item": "https://winchmohamedbahr.com/areas"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": `ونش انقاذ ${displayName}`,
        "item": `https://winchmohamedbahr.com/areas/${toArabicSlug(canonicalName)}`
      }
    ]
  };

  const faqSchema = areaData.faqs && areaData.faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": areaData.faqs.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": f.a
      }
    }))
  } : null;

  const jsonLdData = faqSchema ? [localBusinessSchema, breadcrumbSchema, faqSchema] : [localBusinessSchema, breadcrumbSchema];

  const commonProps = {
    areaData,
    displayName,
    phone,
    brand,
    images: [wa1, wa2, wa3, wa4, wa5]
  };

  return (
    <div className="area-detail-page" style={{
      minHeight: '100vh',
      background: '#f7f7f7',
      fontFamily: 'Tajawal, Arial, sans-serif',
      paddingBottom: '2rem'
    }}>
      <SEO
        title={`${areaData.title} | خصم 50% | خدمة 24 ساعة`}
        description={areaData.metaDescription}
        path={`/areas/${toArabicSlug(canonicalName)}`}
        jsonLd={jsonLdData}
      />
      {/* Side Navigator */}
      <aside className={`side-navigator ${isNavigatorOpen ? 'open' : ''}`} aria-label="شريط التنقل السريع">
        <button
          type="button"
          className="navigator-toggle"
          aria-label={isNavigatorOpen ? "إغلاق قائمة التنقل" : "فتح قائمة التنقل"}
          aria-expanded={isNavigatorOpen}
          onClick={() => setIsNavigatorOpen(!isNavigatorOpen)}
        >
          {isNavigatorOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
        <div className="navigator-content">
          <h3 className="navigator-title">تنقل سريع</h3>
          <nav className="navigator-nav" aria-label="أقسام الصفحة">
            {sections.map((section) => (
              <button
                key={section.id}
                type="button"
                className={`navigator-item ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => scrollToSection(section.id)}
              >
                <span className="navigator-icon">{section.icon}</span>
                <span className="navigator-text">{section.title}</span>
              </button>
            ))}
          </nav>
        </div>
      </aside>

      {/* Dynamic Template Rendering */}
      {templateType === 'MajorDistrict' && <MajorDistrictTemplate {...commonProps} />}
      {templateType === 'Highway' && <HighwayTemplate {...commonProps} />}
      {templateType === 'MidTier' && <MidTierTemplate {...commonProps} />}
      {templateType === 'NewCity' && <NewCityTemplate {...commonProps} />}
      {templateType === 'Governorate' && <GovernorateTemplate {...commonProps} />}

      {/* Common Contact & Gallery Sections appended below the template */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
        {/* Contact Methods */}
        <section id="contact" aria-labelledby="contact-heading" style={{
          background: 'white', borderRadius: '1.5rem', padding: '2rem',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)', marginBottom: '2rem', border: '2px solid #e3f0ff'
        }}>
          <h2 id="contact-heading" style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0a2c61', marginBottom: '1.5rem', textAlign: 'center', direction: 'rtl' }}>
            طرق التواصل لطلب ونش {displayName}
          </h2>
          <address style={{ fontStyle: 'normal', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <a href={`tel:${phone}`} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', borderRadius: '1rem', background: '#f8f9fa', textDecoration: 'none', color: 'inherit', border: '1px solid #e9ecef' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '50px', height: '50px', borderRadius: '50%', background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <Phone size={24} color="#0077ff" />
              </div>
              <div style={{ flex: 1, direction: 'rtl' }}>
                <div style={{ fontWeight: 700, color: '#0a2c61', fontSize: '1.1rem', marginBottom: '0.2rem' }}>رقم الطوارئ السريع</div>
                <div style={{ color: '#0077ff', fontWeight: 600, fontSize: '1rem' }}>{phone}</div>
              </div>
            </a>
            <a href={`https://wa.me/201044844492`} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', borderRadius: '1rem', background: '#f8f9fa', textDecoration: 'none', color: 'inherit', border: '1px solid #e9ecef' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '50px', height: '50px', borderRadius: '50%', background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <FontAwesomeIcon icon={faWhatsapp} size="lg" style={{ color: "#25D366" }} />
              </div>
              <div style={{ flex: 1, direction: 'rtl' }}>
                <div style={{ fontWeight: 700, color: '#0a2c61', fontSize: '1.1rem', marginBottom: '0.2rem' }}>واتساب المباشر</div>
                <div style={{ color: '#25D366', fontWeight: 600, fontSize: '1rem' }}>{phone}</div>
              </div>
            </a>
          </address>
        </section>

        {/* WhatsApp Images Gallery */}
        <section id="gallery" aria-labelledby="gallery-heading" style={{
          background: 'white', borderRadius: '1.5rem', padding: '2rem',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)', border: '2px solid #e3f0ff'
        }}>
          <h2 id="gallery-heading" style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0a2c61', marginBottom: '1.5rem', textAlign: 'center', direction: 'rtl' }}>
            أوناشنا متواجدة في {displayName}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            {[wa1, wa2, wa3, wa4, wa5].map((img, idx) => (
              <figure key={idx} style={{ margin: 0, borderRadius: '1rem', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <img src={img} alt={`ونش انقاذ سيارات ${displayName} - أسطول محمد بحر`} style={{ width: '100%', height: '150px', objectFit: 'cover', display: 'block' }} loading="lazy" />
              </figure>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AreaDetail;